/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback } from '@web-stories-wp/react';
import type {
  CustomFontData,
  FontData,
} from '@src/web-stories-wp/story-editor/src/elements/types';

/**
 * Internal dependencies
 */
import getGoogleFontURL from '../../../utils/getGoogleFontURL';
import getFontCSS from '../../../utils/getFontCSS';
import loadStylesheet from '../../../utils/loadStylesheet';
import { getFontMetricOverrides } from '../../../elements/text/utils/selectFontMetrics';
import FontsService from '../fontsService';
import getFontLoadKey from '../getFontLoadKey';
import type { FontConfig, FontLoadStatus } from '../types';
import type { Dispatch, SetStateAction } from 'react';

function ensureFontLoaded(fontFaceSet: string, content: string) {
  if (typeof document === 'undefined' || !document.fonts) {
    return Promise.resolve(true);
  }

  return document.fonts
    .load(fontFaceSet, content || '')
    .then(() => document.fonts.check(fontFaceSet, content || ''));
}

function loadInlineStylesheet(
  id: string,
  css: string,
  identityKey: string,
  renderFamily: string
) {
  if (!css) {
    return;
  }

  document.head
    .querySelectorAll<HTMLStyleElement>('style[data-st-font-identity]')
    .forEach((style) => {
      if (style.dataset.stFontIdentity === identityKey && style.id !== id) {
        if (style.dataset.stFontFamily === renderFamily) {
          style.remove();
        }
      }
    });

  const style = document.createElement('style');
  style.textContent = css;
  style.id = id;
  style.dataset.stFontIdentity = identityKey;
  style.dataset.stFontFamily = renderFamily;
  document.head.appendChild(style);
}

type FontLoadOptions = Pick<
  FontConfig,
  'renderFamily' | 'revision' | 'identityKey'
>;

export async function maybeLoadFont(
  font: FontData,
  { renderFamily, revision, identityKey }: FontLoadOptions = {}
) {
  if (typeof document === 'undefined') {
    return;
  }

  const { family, service, variants } = font;
  const effectiveFamily = renderFamily || family;
  const handle = [effectiveFamily, identityKey, revision]
    .filter(Boolean)
    .map((value) => encodeURIComponent(value!))
    .join('-');
  const elementId = `web-stories-${handle}-font-css`;
  const stableIdentityKey = identityKey || effectiveFamily;

  const hasFontLink = () => document.getElementById(elementId);

  if (hasFontLink()) {
    return;
  }

  try {
    switch (service) {
      case FontsService.google:
        await loadStylesheet(
          getGoogleFontURL([{ family, variants }], 'auto'),
          elementId
        );
        break;
      case FontsService.custom: {
        const customFont = font as CustomFontData;
        loadInlineStylesheet(
          elementId,
          getFontCSS(
            effectiveFamily,
            customFont.url,
            customFont.urls,
            getFontMetricOverrides(customFont)
          ) || '',
          stableIdentityKey,
          effectiveFamily
        );
        break;
      }
      default:
        return;
    }
  } catch (error) {
    document.getElementById(elementId)?.remove();
    throw error;
  }
}

function useLoadFontFiles({
  setLoadedFonts,
  setFontLoadStatuses,
}: {
  setLoadedFonts: Dispatch<SetStateAction<Omit<FontConfig, 'content'>[]>>;
  setFontLoadStatuses?: Dispatch<
    SetStateAction<Record<string, FontLoadStatus>>
  >;
}) {
  /**
   * Enqueue a list of given fonts by adding <link> or <style> elements.
   *
   * Allows dynamically enqueuing font styles when needed.
   *
   * @param {Array} fonts An array of fonts properties to create a valid FontFaceSet to inject and preload a font-face
   * @return {Promise<boolean>} Returns fonts loaded promise
   */
  const maybeEnqueueFontStyle = useCallback(
    (fonts: FontConfig[]) => {
      return Promise.allSettled(
        fonts.map(
          async ({
            font,
            fontWeight,
            fontStyle,
            content,
            renderFamily,
            revision,
            identityKey,
          }) => {
            const { family, service } = font;
            if (!family || service === FontsService.system) {
              return null;
            }

            const effectiveFamily = renderFamily || family;
            const loadKey = getFontLoadKey({
              font,
              fontWeight,
              fontStyle,
              renderFamily,
              revision,
              identityKey,
            });
            const setLoadStatus = (status: FontLoadStatus) =>
              setFontLoadStatuses?.((statuses) =>
                statuses[loadKey] === status
                  ? statuses
                  : { ...statuses, [loadKey]: status }
              );

            const fontFaceSet = `
              ${fontStyle || ''} ${fontWeight || ''} 0 ${JSON.stringify(
              effectiveFamily
            )}
            `.trim();

            setLoadStatus('pending');
            let fontLoaded = false;
            try {
              await maybeLoadFont(font, {
                renderFamily,
                revision,
                identityKey,
              });
              fontLoaded = await ensureFontLoaded(fontFaceSet, content);
            } catch (error) {
              setLoadStatus('failed');
              throw error;
            }

            if (fontLoaded) {
              setLoadStatus('loaded');
              setLoadedFonts((loadedFonts) => {
                if (
                  loadedFonts.some(
                    (loadedFont) =>
                      (loadedFont.renderFamily || loadedFont.font.family) ===
                        effectiveFamily &&
                      loadedFont.revision === revision &&
                      loadedFont.identityKey === identityKey &&
                      loadedFont.fontWeight === fontWeight &&
                      loadedFont.fontStyle === fontStyle
                  )
                ) {
                  return loadedFonts;
                }

                return [
                  ...loadedFonts,
                  {
                    font,
                    fontWeight,
                    fontStyle,
                    renderFamily,
                    revision,
                    identityKey,
                  },
                ];
              });
            } else {
              setLoadStatus('failed');
            }
          }
        )
      );
    },
    [setLoadedFonts, setFontLoadStatuses]
  );

  return maybeEnqueueFontStyle;
}

export default useLoadFontFiles;
